import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Loader, AlertCircle, Sparkles, PartyPopper } from 'lucide-react';
import './SubscriptionSuccessPage.css';

type Status = 'loading' | 'success' | 'pending' | 'error';

// Simple confetti component
function Confetti() {
    return (
        <div className="confetti-container">
            {[...Array(50)].map((_, i) => (
                <div
                    key={i}
                    className="confetti-piece"
                    style={{
                        left: `${Math.random() * 100}%`,
                        animationDelay: `${Math.random() * 3}s`,
                        animationDuration: `${3 + Math.random() * 2}s`,
                        backgroundColor: ['#14b8a6', '#fbbf24', '#8b5cf6', '#f472b6', '#3b82f6'][Math.floor(Math.random() * 5)],
                    }}
                />
            ))}
        </div>
    );
}

export default function SubscriptionSuccessPage() {
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState<Status>('loading');
    const [showConfetti, setShowConfetti] = useState(false);
    const reference = searchParams.get('ref');

    useEffect(() => {
        // Clean up selected plan from session storage
        sessionStorage.removeItem('selectedPlan');

        // In a real scenario, we'd verify the payment status with backend
        // For now, we'll show success after a short delay
        const timer = setTimeout(() => {
            setStatus('success');
            setShowConfetti(true);

            // Hide confetti after 5 seconds
            setTimeout(() => setShowConfetti(false), 5000);
        }, 2000);

        return () => clearTimeout(timer);
    }, [reference]);

    return (
        <div className="subscription-success-page">
            {showConfetti && <Confetti />}

            <div className="success-card">
                {status === 'loading' && (
                    <>
                        <div className="status-icon-wrapper loading">
                            <Loader className="status-icon" size={48} />
                        </div>
                        <h1>Verificando tu pago...</h1>
                        <p>Estamos confirmando tu transacción con Wompi. Esto toma solo unos segundos.</p>
                        <div className="loading-bar">
                            <div className="loading-bar-fill"></div>
                        </div>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div className="status-icon-wrapper success">
                            <CheckCircle className="status-icon" size={48} />
                            <Sparkles className="sparkle sparkle-1" size={20} />
                            <Sparkles className="sparkle sparkle-2" size={16} />
                            <Sparkles className="sparkle sparkle-3" size={14} />
                        </div>
                        <div className="celebration-emoji">
                            <PartyPopper className="party-icon" size={32} />
                        </div>
                        <h1>¡Pago Exitoso!</h1>
                        <p className="success-message">
                            Tu suscripción ha sido <strong>activada correctamente</strong>.
                            Ya puedes disfrutar de todas las funciones de tu nuevo plan.
                        </p>
                        {reference && (
                            <div className="reference-box">
                                <span className="reference-label">Referencia de pago</span>
                                <code className="reference-code">{reference}</code>
                            </div>
                        )}
                        <Link to="/app" className="dashboard-button">
                            <span>Ir al Dashboard</span>
                            <span className="button-arrow">→</span>
                        </Link>
                    </>
                )}

                {status === 'pending' && (
                    <>
                        <div className="status-icon-wrapper pending">
                            <Loader className="status-icon" size={48} />
                        </div>
                        <h1>Pago en Proceso</h1>
                        <p>Tu pago está siendo procesado. Te notificaremos cuando esté listo.</p>
                        <Link to="/app" className="dashboard-button secondary">
                            Volver al Dashboard
                        </Link>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className="status-icon-wrapper error">
                            <AlertCircle className="status-icon" size={48} />
                        </div>
                        <h1>Hubo un problema</h1>
                        <p>No pudimos verificar tu pago. Si el dinero fue debitado, tu suscripción se activará en breve.</p>
                        <Link to="/app" className="dashboard-button secondary">
                            Volver al Dashboard
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}
